/**
 * Photo looks — the one-tap filters of Zazzle's image effects (their list is
 * No filter, Black & White, Sepia and a dozen named colour grades).
 *
 * Ours are our own recipes with our own names. Each is a single 4×5 colour
 * matrix composed from a few plain adjustments — saturation, per-channel warmth,
 * contrast, a lift of the blacks (fade) and a colour cast — so a look is exact,
 * cheap, identical in the editor preview and the print file, and needs no
 * lookup table or service. A shopper's answer stores only the look's `id`; the
 * matrix is resolved from this table when the zone is expanded, so a recipe can
 * be corrected centrally and old answers still validate.
 *
 * Matrix layout is Skia's: rows R, G, B, A of [r g b a offset], offsets in the
 * 0–1 range.
 */

export type ColorMatrix = number[];

const IDENTITY: ColorMatrix = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0];

/** `outer ∘ inner`: the matrix that applies `inner` first, then `outer`. */
export function composeColorMatrix(outer: ColorMatrix, inner: ColorMatrix): ColorMatrix {
  const out = new Array<number>(20).fill(0);
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 4; col++) {
      let sum = 0;
      for (let k = 0; k < 4; k++) sum += outer[row * 5 + k]! * inner[k * 5 + col]!;
      out[row * 5 + col] = sum;
    }
    let offset = outer[row * 5 + 4]!;
    for (let k = 0; k < 4; k++) offset += outer[row * 5 + k]! * inner[k * 5 + 4]!;
    out[row * 5 + 4] = offset;
  }
  return out;
}

function chain(...steps: ColorMatrix[]): ColorMatrix {
  // Listed in the order they are applied.
  return steps.reduce((acc, step) => composeColorMatrix(step, acc), IDENTITY).map((n) => Math.round(n * 1e5) / 1e5);
}

const LUMA = [0.2126, 0.7152, 0.0722] as const;

function saturate(s: number): ColorMatrix {
  const [r, g, b] = LUMA.map((w) => (1 - s) * w) as [number, number, number];
  return [r + s, g, b, 0, 0, r, g + s, b, 0, 0, r, g, b + s, 0, 0, 0, 0, 0, 1, 0];
}

function channels(r: number, g: number, b: number): ColorMatrix {
  return [r, 0, 0, 0, 0, 0, g, 0, 0, 0, 0, 0, b, 0, 0, 0, 0, 0, 1, 0];
}

function contrast(k: number): ColorMatrix {
  const o = (1 - k) / 2;
  return [k, 0, 0, 0, o, 0, k, 0, 0, o, 0, 0, k, 0, o, 0, 0, 0, 1, 0];
}

/** Lifts the blacks and lowers the whites towards grey: the "faded print" look. */
function fade(f: number): ColorMatrix {
  const k = 1 - f;
  const o = f / 2;
  return [k, 0, 0, 0, o, 0, k, 0, 0, o, 0, 0, k, 0, o, 0, 0, 0, 1, 0];
}

function cast(r: number, g: number, b: number): ColorMatrix {
  return [1, 0, 0, 0, r, 0, 1, 0, 0, g, 0, 0, 1, 0, b, 0, 0, 0, 1, 0];
}

const SEPIA_TONE: ColorMatrix = [
  0.393, 0.769, 0.189, 0, 0,
  0.349, 0.686, 0.168, 0, 0,
  0.272, 0.534, 0.131, 0, 0,
  0, 0, 0, 1, 0,
];

/** A partial sepia: `amount` of the way from the photo to full sepia. */
function sepiaMix(amount: number): ColorMatrix {
  return IDENTITY.map((v, i) => v * (1 - amount) + SEPIA_TONE[i]! * amount);
}

export interface DesignerLook {
  id: string;
  /** Serbian chip label. */
  name: string;
  matrix: ColorMatrix;
}

export const DESIGNER_LOOKS: readonly DesignerLook[] = [
  { id: 'warm', name: 'Toplo', matrix: chain(channels(1.08, 1.02, 0.88), saturate(1.1)) },
  { id: 'cool', name: 'Hladno', matrix: chain(channels(0.9, 1, 1.1), saturate(0.95)) },
  { id: 'faded', name: 'Izbledelo', matrix: chain(saturate(0.8), contrast(0.9), fade(0.12)) },
  { id: 'retro', name: 'Retro', matrix: chain(sepiaMix(0.35), channels(1.04, 1, 0.94), fade(0.06)) },
  { id: 'noir', name: 'Noar', matrix: chain(saturate(0), contrast(1.35)) },
  { id: 'pastel', name: 'Pastel', matrix: chain(saturate(0.7), channels(1.08, 1.08, 1.08), fade(0.1)) },
  { id: 'vivid', name: 'Živopisno', matrix: chain(saturate(1.35), contrast(1.12)) },
  { id: 'golden', name: 'Zlatni sat', matrix: chain(channels(1.1, 1.02, 0.8), contrast(1.05)) },
  { id: 'mint', name: 'Menta', matrix: chain(channels(0.92, 1.06, 1), saturate(0.9), fade(0.05)) },
  { id: 'rose', name: 'Ruža', matrix: chain(channels(1.06, 0.96, 1.02), fade(0.06)) },
  { id: 'film', name: 'Film', matrix: chain(saturate(0.85), contrast(1.1), cast(0.02, 0, -0.02)) },
  { id: 'drama', name: 'Dramatično', matrix: chain(saturate(1.1), contrast(1.4)) },
];

export type DesignerLookId = string;

export function designerLook(id: unknown): DesignerLook | undefined {
  return typeof id === 'string' ? DESIGNER_LOOKS.find((look) => look.id === id) : undefined;
}

/** A matrix a renderer may take: 20 finite numbers of sane magnitude. */
export function validColorMatrix(matrix: unknown): matrix is ColorMatrix {
  return Array.isArray(matrix) && matrix.length === 20
    && matrix.every((n) => typeof n === 'number' && Number.isFinite(n) && Math.abs(n) <= 16);
}

export const DESIGNER_LOOK_CAPABILITIES = {
  effect: "{ kind: 'look', id } on an image layer's effects; one look at a time, replaces the previous look and grayscale/sepia",
  ids: DESIGNER_LOOKS.map((look) => look.id),
  names: Object.fromEntries(DESIGNER_LOOKS.map((look) => [look.id, look.name])),
  rendering: 'Resolved to a native colorMatrix image effect at expansion; identical editor preview, product preview and print.',
} as const;
